import { memo, ReactNode } from 'react';
import { IconName } from '@cbhq/cds-common';
import { Link } from '@cbhq/cds-web/typography/Link';
import { useUSDCRewards, USDCRewardsEligibility } from '@/hooks/useUSDCRewards';
import { PromotionalBanner, PromotionalBannerColor } from './PromotionalBanner';

/**
 * Props for the USDCRewardsBanner component
 */
export type USDCRewardsBannerProps = {
  /**
   * Title text variants for different user types
   * - retail: Title shown to retail users
   * - insto: Title shown to institutional users
   */
  title: {
    retail: string;
    insto: string;
  };
  /**
   * Message content variants for different user types
   * - retail: Message shown to retail users (typically "earn back 2%")
   * - insto: Message shown to institutional users (typically "earn up to 2%")
   */
  message: {
    retail: string;
    insto: string;
  };
  /**
   * Optional learn more link text variants for different user types
   * If provided, a clickable link will be appended to the message
   */
  learnMore?: {
    retail: {
      text: string;
      url: string;
    };
    insto: {
      text: string;
      url: string;
    };
  };
  primaryAction?: ReactNode;
  color?: PromotionalBannerColor;
  /** Optional test ID for testing purposes */
  testID?: string;
};

/**
 * USDCRewardsBanner - A specialized promotional banner for USDC rewards
 *
 * This component displays different content based on the user's USDC rewards eligibility:
 * - Retail users see "earn back 2%" messaging
 * - Institutional users see "earn up to 2%" messaging
 * - Users not eligible for USDC rewards see nothing (component returns null)
 *
 * The component automatically handles eligibility checking and content selection,
 * making it easy to use throughout the application wherever USDC rewards
 * promotional content is needed.
 *
 * When learnMore is provided, a clickable "Learn more" link will be appended
 * to the message content with user-type-specific URLs.
 *
 * @param props - Component props containing title/message variants, optional link props, and testID
 * @returns JSX element or null if user is not eligible for USDC rewards
 */
export const USDCRewardsBanner = memo(function USDCRewardsBanner({
  title,
  message,
  learnMore,
  primaryAction,
  color = 'default',
  testID,
}: USDCRewardsBannerProps) {
  // Get the user's USDC rewards eligibility status
  const usdcRewardsStatus = useUSDCRewards();

  // Early return if user is not eligible for any USDC rewards
  // This ensures the banner only shows to eligible users
  if (usdcRewardsStatus === USDCRewardsEligibility.NA) {
    return null;
  }

  // Determine if user is retail (vs institutional)
  // This determines which variant of title/message to show
  const isRetail = usdcRewardsStatus === USDCRewardsEligibility.RETAIL_USDC_REWARDS;

  // Select appropriate content based on user type
  const selectedTitle = isRetail ? title.retail : title.insto;
  const selectedMessage = isRetail ? message.retail : message.insto;
  const selectedLearnMore = isRetail ? learnMore?.retail : learnMore?.insto;

  // Build the complete description with optional learn more link
  const description = (
    <>
      {selectedMessage}
      {selectedLearnMore && (
        <>
          {' '}
          <Link href={selectedLearnMore.url} target="_blank">
            {selectedLearnMore.text}
          </Link>
        </>
      )}
    </>
  );

  // Render the promotional banner with USDC-specific styling
  // Uses green color scheme to match USDC rewards branding
  return (
    <PromotionalBanner
      icon={'earn' as IconName} // Earn icon to represent rewards/earning
      title={selectedTitle}
      description={description}
      color={color}
      testID={testID}
      primaryAction={primaryAction}
    />
  );
});
